import React, { useState } from "react";
import { api, formatApiError } from "../lib/apiClient";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

const TIPOS = ["Ley", "Decreto", "Resolución", "Circular", "Acuerdo", "Sentencia"];
const ENTITY_TYPES = [
  { v: "nacional", l: "Nacional" },
  { v: "territorial_municipal", l: "Territorial municipal" },
  { v: "territorial_departamental", l: "Territorial departamental" },
  { v: "descentralizada", l: "Descentralizada" },
  { v: "empresa_industrial_comercial_estado", l: "Empresa industrial/comercial del Estado" },
  { v: "otra", l: "Otra" },
];

/**
 * RF-04 — Modal: usuario propone norma aplicable (queda `pendiente` para review admin).
 */
export function ProposeNormaDialog({ open, onOpenChange, onSubmitted }) {
  const [form, setForm] = useState({
    tipo: "Decreto",
    numero: "",
    anio: new Date().getFullYear(),
    alias: "",
    entidad_emisora: "",
    url_oficial: "",
    aplicabilidad: "",
    aplicable_entity_types: [],
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleType = (t) => {
    setForm((f) => ({
      ...f,
      aplicable_entity_types: f.aplicable_entity_types.includes(t)
        ? f.aplicable_entity_types.filter((x) => x !== t)
        : [...f.aplicable_entity_types, t],
    }));
  };

  const resetForm = () => {
    setForm({
      tipo: "Decreto",
      numero: "",
      anio: new Date().getFullYear(),
      alias: "",
      entidad_emisora: "",
      url_oficial: "",
      aplicabilidad: "",
      aplicable_entity_types: [],
    });
    setError("");
  };

  const canSubmit =
    !!form.tipo &&
    form.numero.trim().length >= 1 &&
    Number.isInteger(Number(form.anio)) &&
    Number(form.anio) >= 1800 &&
    Number(form.anio) <= 2100 &&
    form.alias.trim().length >= 3 &&
    form.entidad_emisora.trim().length >= 2 &&
    form.url_oficial.trim().length >= 8 &&
    form.aplicabilidad.trim().length >= 20;

  const onSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError("");
    try {
      const { data: resp } = await api.post("/normativa/submit", {
        tipo: form.tipo.trim(),
        numero: form.numero.trim(),
        anio: Number(form.anio),
        alias: form.alias.trim(),
        entidad_emisora: form.entidad_emisora.trim(),
        url_oficial: form.url_oficial.trim(),
        aplicabilidad: form.aplicabilidad.trim(),
        aplicable_entity_types: form.aplicable_entity_types,
      });
      if (resp.ok === false && resp.code === "already_exists") {
        toast.info(resp.message || "Esta norma ya está registrada.");
      } else {
        toast.success("Norma enviada. Nuestro equipo la revisará pronto.");
      }
      resetForm();
      onOpenChange?.(false);
      onSubmitted?.();
    } catch (e) {
      // 409 duplicate is sent as HTTP error by backend; handle gracefully
      const data = e?.response?.data;
      if (e?.response?.status === 409 && data?.code === "already_exists") {
        toast.info(data.message || "Esta norma ya está registrada.");
        resetForm();
        onOpenChange?.(false);
        onSubmitted?.();
        return;
      }
      setError(formatApiError(e?.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="propose-norma-dialog" className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Proponer norma aplicable</DialogTitle>
          <DialogDescription>
            Envía una propuesta. Un administrador la revisa antes de incluirla.
            No se usa en diagnósticos hasta ser validada.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-2">
          <div className="space-y-1.5">
            <Label>Tipo <span className="text-destructive">✱</span></Label>
            <Select value={form.tipo} onValueChange={(v) => setField("tipo", v)}>
              <SelectTrigger data-testid="propose-tipo" className="rounded-sm h-10">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TIPOS.map((t) => (
                  <SelectItem key={t} value={t}>{t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Número <span className="text-destructive">✱</span></Label>
            <Input
              value={form.numero}
              onChange={(e) => setField("numero", e.target.value)}
              placeholder="1082"
              data-testid="propose-numero"
              className="rounded-sm h-10"
            />
          </div>
          <div className="space-y-1.5">
            <Label>Año <span className="text-destructive">✱</span></Label>
            <Input
              type="number"
              min={1800}
              max={2100}
              value={form.anio}
              onChange={(e) => setField("anio", e.target.value)}
              data-testid="propose-anio"
              className="rounded-sm h-10"
            />
          </div>
          <div className="space-y-1.5">
            <Label>Entidad emisora <span className="text-destructive">✱</span></Label>
            <Input
              value={form.entidad_emisora}
              onChange={(e) => setField("entidad_emisora", e.target.value)}
              placeholder="Presidencia de la República"
              data-testid="propose-emisora"
              className="rounded-sm h-10"
            />
          </div>
          <div className="space-y-1.5 md:col-span-2">
            <Label>Nombre / alias <span className="text-destructive">✱</span></Label>
            <Input
              value={form.alias}
              onChange={(e) => setField("alias", e.target.value)}
              placeholder="Decreto Único Reglamentario Sector Administrativo..."
              data-testid="propose-alias"
              className="rounded-sm h-10"
            />
          </div>
          <div className="space-y-1.5 md:col-span-2">
            <Label>URL oficial <span className="text-destructive">✱</span></Label>
            <Input
              type="url"
              value={form.url_oficial}
              onChange={(e) => setField("url_oficial", e.target.value)}
              placeholder="https://www.funcionpublica.gov.co/..."
              data-testid="propose-url"
              className="rounded-sm h-10"
            />
          </div>
          <div className="space-y-1.5 md:col-span-2">
            <Label>Aplicabilidad / resumen <span className="text-destructive">✱</span></Label>
            <Textarea
              rows={4}
              value={form.aplicabilidad}
              onChange={(e) => setField("aplicabilidad", e.target.value)}
              placeholder="Explica brevemente qué regula y en qué casos aplica (mínimo 20 caracteres)..."
              data-testid="propose-aplicabilidad"
              className="rounded-sm"
            />
          </div>
          <div className="space-y-1.5 md:col-span-2">
            <Label>Tipos de entidad aplicables</Label>
            <div className="flex flex-wrap gap-2">
              {ENTITY_TYPES.map((o) => (
                <button
                  type="button"
                  key={o.v}
                  onClick={() => toggleType(o.v)}
                  className={`text-xs px-3 py-1.5 border rounded-sm ${
                    form.aplicable_entity_types.includes(o.v)
                      ? "border-primary bg-primary text-primary-foreground"
                      : "border-gray-300 text-gray-700 hover:border-gray-400"
                  }`}
                  data-testid={`propose-type-${o.v}`}
                >
                  {o.l}
                </button>
              ))}
            </div>
            <p className="text-[11px] text-gray-500">Sin selección = aplica a todos.</p>
          </div>
        </div>

        {error && (
          <div className="text-sm text-red-700 bg-red-50 border border-red-200 p-2 rounded-sm" data-testid="propose-error">
            {error}
          </div>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => {
              resetForm();
              onOpenChange?.(false);
            }}
            className="rounded-sm"
            data-testid="propose-cancel"
          >
            Cancelar
          </Button>
          <Button
            onClick={onSubmit}
            disabled={submitting || !canSubmit}
            className="rounded-sm"
            data-testid="propose-submit"
          >
            {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Enviar propuesta
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ProposeNormaDialog;
